/* ============================================================
   Empenzo — Small Order Handling Fee  (namespace: epz-sof-*)
   ============================================================
   RULE
     U.S. merchandise subtotal under $50.00 carries a $9.99 Small
     Order Handling Fee. At $50.00 the fee is removed and the $99
     free-shipping progress takes over as the only bar in the drawer.

     This is NOT a minimum order. Orders of any size are accepted.
     The fee is a charge, not a gate — never call it a minimum.

   ------------------------------------------------------------
   WHY THIS FILE WAS REWRITTEN (2026-08-25) — READ BEFORE EDITING
   ------------------------------------------------------------
     The previous version only REACTED to cart writes it could see:
     it patched fetch/XHR, watched for /cart/(add|change|update|clear)
     and corrected the cart afterwards. Anything that reached
     Shopify's checkout WITHOUT a theme-observed cart write escaped it
     completely. Order EPZ-1102 ($16.95 merchandise, no fee) went out
     that way. Two holes were confirmed live:

       1. The product form's dynamic checkout button — "Buy it now",
          Shop Pay, and the "More payment options" link. It builds a
          checkout straight from the product form. No cart is touched,
          no theme page is ever loaded again, so this script never
          runs. Reproduced live: $16.95 at checkout, no fee.

       2. Cart permalinks, /cart/<variant>:<qty>. Shopify redirects
          them STRAIGHT to checkout, skipping the cart page. The Quick
          Order page's "Copy Cart Link" button was handing these out.
          Reproduced live: $16.95 at checkout, no fee.

     Hole 1 is closed at source — show_buy_now is now false in every
     product template, plus a CSS suppression in
     snippets/header-mini-cart.liquid as a second line of defence.
     Hole 2 is closed by appending ?storefront=true to every permalink
     the theme generates (sections/epz-quick-order.liquid), which
     lands the buyer on the CART page instead of checkout.

     This file is the third line of defence, and the only layer that
     can see the real cart. Its job is no longer just "fix the cart
     afterwards" — it is "nothing ENTERS checkout from a theme page
     until the cart has been verified correct".

     A fourth layer, snippets/epz-sof-state.liquid, renders the same
     verdict server-side and ships the Check out button already
     disabled. That is what holds the line before this script has even
     parsed, and if JavaScript never runs at all.

   ------------------------------------------------------------
   NEVER RELOAD THE PAGE FROM HERE
   ------------------------------------------------------------
     Earlier versions of this file called window.location.reload()
     whenever a Section Rendering swap could not be applied, on the
     reasoning that "a reload is always correct; a partial write is
     not". It is not always correct. If the page reloads into a state
     where the very same swap fails again, the reload repeats, and the
     storefront becomes an unusable loop that no shopper can escape.
     A session flag guarding the reload does not save you either: any
     later successful sync clears the flag and re-arms the loop.

     So: no reload, ever. When a swap fails, what is on screen no
     longer matches the cart, and the ONE outcome that must never
     happen is stale totals with checkout open. markRenderStale()
     therefore keeps the gate SHUT and asks the shopper to refresh.
     The refresh is theirs to make, and a page they chose to reload
     cannot loop.

   ------------------------------------------------------------
   WHAT "VERIFIED" MEANS
   ------------------------------------------------------------
     `verified` is true only when a freshly fetched /cart.js satisfies
     diagnose() === null. It is set FALSE the instant any cart write
     STARTS — not when it finishes — so a Check out click that lands
     mid-flight is intercepted rather than raced. Every checkout entry
     point re-checks it:

       • click on any [name="checkout"] submit button
       • submit of any form carrying that button, or posting /checkout
       • click on any link to /checkout
       • click on a /cart/<id>:<qty> permalink (rewritten in place)

     Checkout is offered only when `allow` is true, which is stricter
     than `verified`: the cart must also be non-empty and the rendered
     page must not be stale.

     In practice the gate usually wins before the guard has to: the
     moment verified drops, updateGates() disables the Check out
     button, so the click has nothing to hit. The click guard is the
     backstop for checkout controls that are NOT inside a gate.

   ------------------------------------------------------------
   NEVER VERIFY AGAINST A CART THAT IS STILL MOVING
   ------------------------------------------------------------
     pendingWrites counts cart writes made BY THE THEME that are still
     in flight. run() waits for that count to reach zero before it
     trusts a /cart.js snapshot. Without this, a guard triggered while
     a "remove the fee" request was still travelling could read the
     pre-removal cart, conclude the fee is present, unlock checkout,
     and let the buyer through on a cart that lost the fee a moment
     later. The wait is bounded (WRITE_WAIT_MS) so a request that
     never settles degrades to a slow check, never a permanent hang.

     Our own corrections use the captured nativeFetch and deliberately
     do NOT increment the counter — if they did, run() would wait on
     itself and deadlock.

   ------------------------------------------------------------
   THRESHOLD LIVES IN FOUR PLACES — KEEP THEM IN SYNC
   ------------------------------------------------------------
     1. THRESHOLD_CENTS below                          (this file)
     2. epz_sof_threshold in snippets/epz-small-order-fee.liquid
     3. epz_sof_threshold in snippets/free-shipping-progress.liquid
     4. epz_state_threshold in snippets/epz-sof-state.liquid
     1-3 decide what the customer is told; this file decides whether
     the fee line exists; 4 decides whether checkout is offered.

     The $9.99 figure itself is the PRICE OF THE FEE VARIANT in
     Shopify admin (product "Small Order Handling Fee"). Changing the
     displayed amount in Liquid without repricing the variant, or the
     reverse, makes the cart contradict the message.

   MERCHANDISE SUBTOTAL — PRE-DISCOUNT, FEE EXCLUDED
     Sum of item.original_line_price across every line except the fee.
     original_line_price is the price BEFORE any discount, automatic
     or code. Shipping and taxes are not cart lines, so they are
     excluded by construction — $44.00 of goods with $8.00 of shipping
     is $44.00 here and still carries the fee.

     Pre-discount is deliberate. This fee recovers the cost of picking,
     packing and shipping a physically small order. A customer with $40
     of merchandise who applies a $10 coupon has not made the order any
     cheaper to fulfil and must not be handed a $9.99 fee for using the
     coupon.

     NOTE the deliberate asymmetry with free-shipping-progress.liquid,
     which stays POST-discount because it has to mirror the order-price
     condition on the "Free Shipping (Orders $99+)" delivery rate.
     Shopify evaluates that condition after discounts. The $50 rule is
     our own policy; the $99 rule is Shopify's, and the bar must not
     promise what checkout will refuse.

     Excluding the fee from its own threshold is mandatory. Include it
     and a $45 cart plus $9.99 reads as $54.99, the fee is removed, the
     cart falls back to $45, the fee is re-added — an infinite loop.

   SECTION IDS: READ THEM FROM THE DOM, NEVER HARD-CODE THE FILENAME
     Identical constraint to assets/epz-fsb.js. cart.json is a JSON
     template, so at runtime its sections are named
     template--<n>__main-cart-footer, not main-cart-footer. Asking
     the Ajax API for the bare filename renders the section with NO
     blocks — no subtotal, no Check out button — and writing that in
     wipes them. Both cart sections print their real id into
     data-id="{{ section.id }}"; that is the only source used here.

   NOTE ON THE CART PAGE GATE
     The Section Rendering API swap for #main-cart-footer only
     replaces `.js-contents`. The Check out button and the cart's
     accelerated-checkout block sit OUTSIDE that container, so they
     are never refreshed by a swap. updateGates() therefore drives
     them directly from JS. Do not "fix" this by moving the buttons
     inside .js-contents — the theme's own cart JS targets them where
     they are.

   FAILURE IS NOT A LOCKOUT
     A transient network error must not leave a shopper staring at a
     Check out button that never unlocks. sync() retries a bounded
     number of times; only after MAX_RETRIES does the gate swap its
     message for one that tells the shopper to refresh. Silence would
     be worse than the fee being missed.
   ============================================================ */
(function () {
  if (window.__epzSofReady) return;
  window.__epzSofReady = true;

  var FEE_VARIANT_ID = 48857770950905;
  var FEE_HANDLE = 'small-order-handling-fee';
  var THRESHOLD_CENTS = 5000;

  /* add + remove-extra + fix-quantity is 3; 4 leaves one spare pass.
     Beyond that something is wrong and the gate stays shut. */
  var MAX_PASSES = 4;
  var MAX_RETRIES = 3;
  var WRITE_WAIT_MS = 4000;

  var GATE_ERROR_TEXT =
    'We could not confirm your cart just now. Please refresh the page and try again.';
  var STALE_TEXT =
    'Your cart has changed. Please refresh the page to continue to checkout.';

  /* Mirrors assets/epz-fsb.js. Keep the two in sync if the theme's
     cart markup changes. */
  var SPECS = [
    { node: '#header-mini-cart-content', id: 'header-mini-cart-content', inner: null },
    { node: '#header-mini-cart-footer', id: 'header-mini-cart-footer', inner: null },
    { node: '#main-cart-items', id: 'main-cart-items', inner: '.js-contents' },
    { node: '#main-cart-footer', id: 'main-cart-footer', inner: '.js-contents' }
  ];

  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var PERMALINK = /^\/cart\/\d+(:\d+)?(,\d+(:\d+)?)*\/?$/;

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  var nativeOpen = XMLHttpRequest.prototype.open;

  var verified = true;
  var running = false;
  var queued = false;
  var inFlight = null;
  var bypassOnce = false;
  var pendingWrites = 0;
  var syncFailures = 0;
  var cartEmpty = false;
  var renderStale = false;

  function each(list, fn) {
    Array.prototype.forEach.call(list || [], fn);
  }

  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  function getCart() {
    return nativeFetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error('cart.js ' + r.status);
      return r.json();
    });
  }

  /* Bounded: a write that never settles degrades to a slow check,
     never a permanent hang. */
  function settleWrites() {
    if (pendingWrites <= 0) return Promise.resolve();
    var waited = 0;
    return new Promise(function (resolve) {
      (function poll() {
        if (pendingWrites <= 0 || waited >= WRITE_WAIT_MS) return resolve();
        waited += 60;
        setTimeout(poll, 60);
      })();
    });
  }

  /* ----------------------------------------------------------
     Cart maths — rules unchanged from the original file
     ---------------------------------------------------------- */

  function feeLines(cart) {
    var out = [];
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === FEE_VARIANT_ID) {
        out.push({ item: cart.items[i], index: i + 1 });
      }
    }
    return out;
  }

  /* Pre-discount merchandise subtotal, every fee line excluded. */
  function merchandiseCents(cart) {
    var sum = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (item.variant_id === FEE_VARIANT_ID) continue;
      sum += item.original_line_price;
    }
    return sum;
  }

  /* One correction per call; run() re-diagnoses after each.
       add     — under threshold, fee absent
       remove  — at or over threshold, fee alone in the cart, or a
                 duplicate fee line (always the LAST one, so the
                 indexes of the lines before it do not shift)
       fix     — single fee line present but quantity is not 1
     Returns null when the cart is already correct. */
  function diagnose(cart) {
    var lines = feeLines(cart);
    var merch = merchandiseCents(cart);

    /* No merchandise at all. The fee must never stand alone — a cart
       containing only a handling fee is not a purchase. */
    if (merch === 0) {
      return lines.length ? { action: 'remove', line: lines[lines.length - 1].index } : null;
    }

    if (merch < THRESHOLD_CENTS) {
      if (!lines.length) return { action: 'add' };
      if (lines.length > 1) return { action: 'remove', line: lines[lines.length - 1].index };
      if (lines[0].item.quantity !== 1) return { action: 'fix', line: lines[0].index };
      return null;
    }

    return lines.length ? { action: 'remove', line: lines[lines.length - 1].index } : null;
  }

  /* ----------------------------------------------------------
     The gate — server-rendered by snippets/epz-sof-state.liquid,
     kept in step here.
     ---------------------------------------------------------- */

  function updateGates() {
    /* Stricter than `verified` on purpose. An empty cart is disabled
       by the theme and must STAY disabled; a stale render must not
       offer checkout over figures the shopper can no longer trust. */
    var allow = verified && !cartEmpty && !renderStale;

    each(document.querySelectorAll('[data-epz-sof-gate]'), function (gate) {
      gate.setAttribute('data-epz-sof-gate', allow ? 'ok' : 'blocked');

      each(gate.querySelectorAll('[name="checkout"]'), function (btn) {
        if (allow) {
          /* Only re-enable buttons WE disabled. */
          if (btn.hasAttribute('data-epz-sof-disabled')) {
            btn.removeAttribute('data-epz-sof-disabled');
            btn.removeAttribute('aria-disabled');
            btn.disabled = false;
          }
        } else if (!btn.hasAttribute('data-epz-sof-disabled')) {
          btn.setAttribute('data-epz-sof-disabled', '');
          btn.setAttribute('aria-disabled', 'true');
          btn.disabled = true;
        }
      });

      /* An empty cart shows no explanation — there is nothing to
         explain, and the theme's own empty state already speaks. */
      each(gate.querySelectorAll('[data-epz-sof-gate-note]'), function (note) {
        note.hidden = allow || cartEmpty;
      });
    });

    /* The cart's own accelerated checkout buttons (Shop Pay, PayPal,
       Apple/Google Pay). These are safe once the fee is a line item —
       they carry it through — so they are hidden only while the cart
       is unverified, never removed. */
    each(document.querySelectorAll('[data-epz-sof-express]'), function (node) {
      node.hidden = !allow;
    });
  }

  function setVerified(next) {
    verified = !!next;
    updateGates();
  }

  /* Pass null to put the server-rendered wording back. */
  function setNoteText(text) {
    each(document.querySelectorAll('[data-epz-sof-gate-note]'), function (note) {
      if (text) {
        if (!note.hasAttribute('data-epz-sof-note-original')) {
          note.setAttribute('data-epz-sof-note-original', note.textContent);
        }
        note.textContent = text;
      } else if (note.hasAttribute('data-epz-sof-note-original')) {
        note.textContent = note.getAttribute('data-epz-sof-note-original');
        note.removeAttribute('data-epz-sof-note-original');
      }
    });
  }

  /* The swap failed, so the screen no longer matches the cart. See the
     "NEVER RELOAD THE PAGE FROM HERE" note at the top of this file. */
  function markRenderStale() {
    renderStale = true;
    updateGates();
    setNoteText(STALE_TEXT);
  }

  function readInitialGate() {
    var gate = document.querySelector('[data-epz-sof-gate]');
    if (!gate) return true;
    return gate.getAttribute('data-epz-sof-gate') === 'ok';
  }

  /* ----------------------------------------------------------
     Section rendering
     ---------------------------------------------------------- */

  function plan() {
    var out = [];
    SPECS.forEach(function (s) {
      var el = document.querySelector(s.node);
      if (!el) return;
      out.push({
        node: s.node,
        inner: s.inner,
        section: el.getAttribute('data-id') || s.id
      });
    });
    return out;
  }

  function apply(sections, list) {
    var ok = true;

    list.forEach(function (t) {
      var html = sections[t.section];
      var node = document.querySelector(t.node);
      if (!html || !node) {
        ok = false;
        return;
      }

      var doc = new DOMParser().parseFromString(html, 'text/html');
      var fresh = doc.querySelector(t.node) || doc.getElementById('shopify-section-' + t.section);
      if (!fresh) {
        ok = false;
        return;
      }

      if (t.inner) {
        var current = node.querySelector(t.inner);
        var replacement = fresh.querySelector(t.inner);
        if (!current || !replacement) {
          ok = false;
          return;
        }
        current.innerHTML = replacement.innerHTML;
        return;
      }

      node.innerHTML = fresh.innerHTML;
    });

    return ok;
  }

  /* The header total must never lag the cart. A label still counting a
     fee the guard has just removed is the shopper's first sight of the
     price and reads as an overcharge.

     Order of preference is deliberate. Swapping only the NUMBER inside
     whatever the server already rendered preserves the shop's own money
     format — currency code, symbol position, separators — none of which
     we should be reinventing here. Shopify.formatMoney is the second
     choice because it is absent on this theme and, when present, drops
     the trailing " USD" the server renders. The bare fallback is last. */
  function refreshHeader(cart) {
    var q = document.querySelector('.head-slot-cart-link-quantity');
    if (q) q.textContent = cart.item_count;

    /* MUST stay scoped to .head-slot-cart-link. Two elements in this
       header carry .head-slot-nav-link-label — the Quick Order button
       comes FIRST in document order, the cart total second. An
       unscoped querySelector therefore rewrites the Quick Order
       button's text with the cart total. */
    var label = document.querySelector('.head-slot-cart-link .head-slot-nav-link-label');
    if (!label) return;

    var amount = (cart.total_price / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    var current = label.textContent || '';
    var swapped = current.replace(/\d[\d.,]*/, amount);
    if (swapped !== current) {
      label.textContent = swapped;
      return;
    }

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      label.textContent = '(' + window.Shopify.formatMoney(cart.total_price) + ')';
      return;
    }

    /* Reached when the server rendered no digits to swap — an empty
       cart getting its first item, which is the commonest flow of all.
       Rebuild the shop's format rather than emitting a bare number,
       otherwise the "$" and " USD" are stripped here and every later
       swap chains off the stripped text. Locale is pinned to en-US to
       match what money_with_currency renders for this shop, not to the
       visitor's browser locale. */
    var code = cart.currency || 'USD';
    var pretty = amount;
    try {
      pretty = new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(
        cart.total_price / 100
      );
    } catch (e) {}
    label.textContent = '(' + pretty + ' ' + code + ')';
  }

  function markFeeLine() {
    var links = document.querySelectorAll('a[href*="/products/' + FEE_HANDLE + '"]');
    each(links, function (a) {
      var row = a.closest('tr, li, .cart-item, .mini-cart-item');
      if (row) row.classList.add('epz-sof-locked');
    });
  }

  /* ----------------------------------------------------------
     Correction
     ---------------------------------------------------------- */

  function mutate(fix, opts) {
    var list = plan();
    var sectionNames = list.map(function (t) { return t.section; }).join(',');
    var url;
    var payload;

    if (fix.action === 'add') {
      url = root() + 'cart/add.js';
      payload = { items: [{ id: FEE_VARIANT_ID, quantity: 1 }] };
    } else {
      url = root() + 'cart/change.js';
      payload = { line: fix.line, quantity: fix.action === 'remove' ? 0 : 1 };
    }

    if (sectionNames) {
      payload.sections = sectionNames;
      payload.sections_url = window.location.pathname;
    }

    return nativeFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart write ' + r.status);
        return r.json();
      })
      .then(function (body) {
        if (opts.silent || !sectionNames) return body;
        if (!body.sections || !apply(body.sections, list)) markRenderStale();
        return body;
      });
  }

  function run(opts) {
    var passes = 0;
    var changed = false;

    function step() {
      return settleWrites()
        .then(getCart)
        .then(function (cart) {
          cartEmpty = cart.item_count === 0;

          /* Nothing to buy and nothing to mis-price: a stale render
             stops mattering the moment the cart is empty. */
          if (cartEmpty) renderStale = false;

          var fix = diagnose(cart);

          if (!fix) {
            setVerified(true);
            markFeeLine();
            if (changed) {
              refreshHeader(cart);
              document.dispatchEvent(new CustomEvent('epz:sof:changed', { bubbles: true }));
            }
            return cart;
          }

          setVerified(false);
          if (++passes > MAX_PASSES) return cart;

          changed = true;
          return mutate(fix, opts).then(step);
        });
    }

    return step();
  }

  /* Never drop a sync. The old version returned early while busy, so a
     second cart write landing mid-flight was simply not re-checked —
     the cart could settle wrong and stay wrong. */
  function sync(opts) {
    opts = opts || {};

    if (running) {
      queued = true;
      return inFlight;
    }

    running = true;
    inFlight = run(opts)
      .then(function (result) {
        syncFailures = 0;
        if (!renderStale) setNoteText(null);
        return result;
      })
      .catch(function () {
        setVerified(false);
        syncFailures += 1;
        if (syncFailures < MAX_RETRIES) {
          queued = true;
          return new Promise(function (resolve) {
            setTimeout(resolve, 700 * syncFailures);
          });
        }
        setNoteText(GATE_ERROR_TEXT);
        return null;
      })
      .then(function (result) {
        running = false;
        if (queued) {
          queued = false;
          return sync(opts);
        }
        return result;
      });

    return inFlight;
  }

  /* ----------------------------------------------------------
     Checkout guard
     ---------------------------------------------------------- */

  function allowed() {
    return verified && !cartEmpty && !renderStale;
  }

  function resubmit(form, submitter) {
    if (form.requestSubmit && submitter && submitter.form === form) {
      bypassOnce = true;
      try {
        form.requestSubmit(submitter);
        return;
      } catch (e) {
        bypassOnce = false;
      }
    }

    /* form.submit() fires no submit event, so the capture listener
       below never sees it and no bypass flag is needed. The hidden
       field carries the submitter's name so Shopify still routes the
       POST to checkout rather than treating it as a cart update. */
    if (submitter && submitter.name) {
      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = submitter.name;
      hidden.value = submitter.value || '';
      form.appendChild(hidden);
    }
    form.submit();
  }

  /* After a section swap the button that was clicked is a detached
     node. Re-find the live one inside the same gate before
     resubmitting. */
  function liveCheckout(scope) {
    var sel = scope
      ? '[data-epz-sof-scope="' + scope + '"] [name="checkout"]'
      : '[data-epz-sof-gate] [name="checkout"]';
    return document.querySelector(sel);
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      /* Cart permalinks go straight to checkout. Rewritten in place so
         they land on the cart page, where the gate applies. */
      var link = target.closest('a[href]');
      if (link) {
        var raw = link.getAttribute('href') || '';
        var path = raw;
        try { path = new URL(raw, window.location.origin).pathname; } catch (e) {}

        if (PERMALINK.test(path) && raw.indexOf('storefront=true') === -1) {
          link.setAttribute('href', raw + (raw.indexOf('?') === -1 ? '?' : '&') + 'storefront=true');
        }

        if (!allowed() && /\/checkouts?(\/|$|\?)/.test(path)) {
          var href = link.href;
          event.preventDefault();
          event.stopImmediatePropagation();
          sync({}).then(function () {
            if (allowed()) window.location.href = href;
          });
          return;
        }
      }

      if (bypassOnce) return;

      var btn = target.closest('[name="checkout"]');
      if (!btn || allowed()) return;

      var gate = btn.closest('[data-epz-sof-gate]');
      var scope = gate ? gate.getAttribute('data-epz-sof-scope') : null;
      var form = btn.form || btn.closest('form');

      event.preventDefault();
      event.stopImmediatePropagation();
      setVerified(false);

      sync({}).then(function () {
        if (!allowed()) return;
        var live = liveCheckout(scope) || btn;
        var liveForm = live.form || live.closest('form') || form;
        if (liveForm) resubmit(liveForm, live);
      });
    },
    true
  );

  document.addEventListener(
    'submit',
    function (event) {
      if (bypassOnce) {
        bypassOnce = false;
        return;
      }

      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;

      var submitter = event.submitter;
      var action = form.getAttribute('action') || '';
      var isCheckout =
        !!(submitter && submitter.name === 'checkout') || action.indexOf('/checkout') !== -1;

      if (!isCheckout || allowed()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      setVerified(false);

      sync({}).then(function () {
        if (allowed()) resubmit(form, submitter);
      });
    },
    true
  );

  /* ----------------------------------------------------------
     Triggers

     The theme mutates the cart from several places (product form,
     quantity steppers, line remove, coupon apply, booster cards),
     not all of which publish a usable event. Watching the network
     layer catches every one of them without needing to know which
     component fired. verified is dropped at REQUEST START, not on
     completion, so a Check out click cannot slip through the window
     while a write is in flight.
     ---------------------------------------------------------- */

  if (nativeFetch) {
    window.fetch = function () {
      var url = arguments[0];
      if (url && url.url) url = url.url;
      var watch = typeof url === 'string' && CART_WRITE.test(url);

      if (!watch) return nativeFetch.apply(window, arguments);

      pendingWrites += 1;
      setVerified(false);

      return nativeFetch.apply(window, arguments).then(
        function (response) {
          pendingWrites -= 1;
          if (response.ok) setTimeout(function () { sync({}); }, 0);
          else setVerified(false);
          return response;
        },
        function (error) {
          pendingWrites -= 1;
          setVerified(false);
          throw error;
        }
      );
    };
  }

  XMLHttpRequest.prototype.open = function (method, url) {
    if (typeof url === 'string' && CART_WRITE.test(url)) {
      setVerified(false);

      /* loadstart, not open: it fires on send(), so a request that is
         opened but never sent cannot leak the counter. */
      this.addEventListener('loadstart', function () { pendingWrites += 1; });
      this.addEventListener('loadend', function () {
        pendingWrites -= 1;
        if (this.status >= 200 && this.status < 300) {
          setTimeout(function () { sync({}); }, 0);
        } else {
          setVerified(false);
        }
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  document.addEventListener('epz:fsb:added', function () { sync({}); });
  document.addEventListener('cart:item-add', function () { setVerified(false); sync({}); });

  /* Page load, and every restore from the back/forward cache — a
     customer who reaches checkout, backs out, and edits the cart
     must not land on a stale fee state. */
  function boot() {
    verified = readInitialGate();
    updateGates();
    sync({});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    renderStale = false;
    setVerified(false);
    sync({});
  });

  window.epzSof = {
    sync: sync,
    threshold: THRESHOLD_CENTS,
    variant: FEE_VARIANT_ID,
    isVerified: function () { return verified; },
    isAllowed: allowed,
    pendingWrites: function () { return pendingWrites; }
  };
})();